import { useState, useEffect } from 'react';
import { devicesApi } from '../../api/devices.api';
import { Link } from 'react-router-dom';

interface ProblematicDevice {
  _id: string;
  serialNumber: string;
  name?: string;
  product?: {
    _id: string;
    name: string;
    brand?: any;
    category?: any;
    model?: any;
  };
  branch?: {
    _id: string;
    name: string;
  };
  company?: {
    _id: string;
    name: string;
  };
  status: string;
  issues: string[];
}

const ProblematicList = () => {
  const [devices, setDevices] = useState<ProblematicDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterIssue, setFilterIssue] = useState<string>('all');

  useEffect(() => {
    fetchProblematicDevices();
  }, []);

  const fetchProblematicDevices = async () => {
    try {
      setIsLoading(true);
      const response = await devicesApi.getProblematic();
      setDevices(response);
    } catch (error) {
      console.error('Sorunlu cihazlar yüklenirken hata:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredDevices = devices.filter(device => {
    const matchesSearch = 
      device.serialNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.product?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      device.name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = 
      filterIssue === 'all' || 
      device.issues.some(issue => issue.includes(filterIssue));

    return matchesSearch && matchesFilter;
  });

  const getIssueColor = (issue: string) => {
    if (issue.includes('Ürün')) return 'bg-red-100 text-red-800';
    if (issue.includes('Marka')) return 'bg-orange-100 text-orange-800';
    if (issue.includes('Kategori')) return 'bg-yellow-100 text-yellow-800';
    if (issue.includes('Model')) return 'bg-blue-100 text-blue-800';
    if (issue.includes('Şube')) return 'bg-purple-100 text-purple-800';
    if (issue.includes('Şirket')) return 'bg-pink-100 text-pink-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      Available: { label: 'Şubede', className: 'bg-emerald-100 text-emerald-800' },
      Assigned: { label: 'Zimmetli', className: 'bg-blue-100 text-blue-800' },
      InRepair: { label: 'Onarımda', className: 'bg-orange-100 text-orange-800' },
      Scrapped: { label: 'Hurda', className: 'bg-red-100 text-red-800' },
    };
    const statusInfo = statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-20 bg-gray-100 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-1">Sorunlu Cihazlar</h1>
              <p className="text-sm text-gray-500">
                Eksik veya hatalı bilgilere sahip {devices.length} cihaz bulundu
              </p>
            </div>
            <button
              onClick={fetchProblematicDevices}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Yenile
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {devices.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: 'Ürün Eksik', key: 'Ürün', color: 'bg-red-50 border-red-200' },
              { label: 'Marka Eksik', key: 'Marka', color: 'bg-orange-50 border-orange-200' },
              { label: 'Kategori Eksik', key: 'Kategori', color: 'bg-yellow-50 border-yellow-200' },
              { label: 'Model Eksik', key: 'Model', color: 'bg-blue-50 border-blue-200' },
              { label: 'Şube Eksik', key: 'Şube', color: 'bg-purple-50 border-purple-200' },
              { label: 'Şirket Eksik', key: 'Şirket', color: 'bg-pink-50 border-pink-200' },
            ].map((item) => {
              const count = devices.filter(d => 
                d.issues.some(issue => issue.includes(item.key))
              ).length;
              return (
                <div key={item.key} className={`rounded-lg border p-4 ${item.color}`}>
                  <p className="text-xs text-gray-600 mb-1">{item.label}</p>
                  <p className="text-2xl font-semibold text-gray-900">{count}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ara</label>
              <input
                type="text"
                placeholder="Seri no veya ürün adı..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sorun Türü</label>
              <select
                value={filterIssue}
                onChange={(e) => setFilterIssue(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="all">Tümü</option>
                <option value="Ürün">Ürün Bulunamadı</option>
                <option value="Marka">Marka Bulunamadı</option>
                <option value="Kategori">Kategori Bulunamadı</option>
                <option value="Model">Model Bulunamadı</option>
                <option value="Şube">Şube Bulunamadı</option>
                <option value="Şirket">Şirket Bulunamadı</option>
              </select>
            </div>
          </div>
        </div>

        {/* Devices List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {filteredDevices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-6">
              <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-1">Sorunlu cihaz bulunamadı</h3>
              <p className="text-sm text-gray-500">Tüm cihazlar eksiksiz bilgilere sahip</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cihaz Bilgisi
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Şube / Şirket
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Durum
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sorunlar
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İşlem
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDevices.map((device) => (
                    <tr key={device._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {device.product?.name || device.name || 'İsimsiz Cihaz'}
                          </span>
                          <span className="text-xs text-gray-500">
                            SN: {device.serialNumber || 'Yok'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-900">
                            {device.branch?.name || '-'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {device.company?.name || '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(device.status)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {device.issues.map((issue, idx) => (
                            <span
                              key={idx}
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getIssueColor(issue)}`}
                            >
                              {issue}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          to={`/devices/${device._id}`}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Düzenle
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProblematicList;
